import { ApiError, toProblemDetails } from './errors';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type QueryValue = string | number | boolean | null | undefined | Array<string | number>;

export interface RequestOptions {
    query?: Record<string, QueryValue>;
    body?: unknown;
    headers?: Record<string, string>;
    signal?: AbortSignal;
    /** Wordt als `Idempotency-Key` meegestuurd; maakt een POST veilig herhaalbaar. */
    idempotencyKey?: string;
    timeoutMs?: number;
    retryAttempts?: number;
    /** Sla het GET-antwoord op onder deze sleutel en serveer hem offline. */
    cacheKey?: string | false;
    /**
     * Een anoniem verzoek: geen token, geen `X-Tenant`, geen apparaat, en een
     * 401 is dan een antwoord en geen verlopen sessie.
     */
    skipAuth?: boolean;
    /**
     * Stuur dit token mee in plaats van het huidige (bijvoorbeeld het eigen token
     * tijdens een impersonatie). De omgeving volgt dan uit het token, dus zonder
     * `X-Tenant`.
     */
    token?: string;
    expect?: 'json' | 'blob' | 'none';
}

export interface ApiResult<T> {
    data: T;
    status: number;
    /** True als het antwoord uit de offline cache komt in plaats van van de server. */
    fromCache: boolean;
}

/**
 * Offline cache voor GET-verzoeken. De Dexie-implementatie staat in
 * `shared/offline`, zodat deze module niets van de database hoeft te weten.
 */
export interface OfflineCache {
    read(key: string): Promise<unknown | undefined>;
    write(key: string, value: unknown): Promise<void>;
}

export interface HttpClientOptions {
    baseUrl: string;
    getToken?: () => string | null | undefined;
    getTenantId?: () => string | null | undefined;
    getDeviceId?: () => string | null | undefined;
    fetchImpl?: typeof fetch;
    isOnline?: () => boolean;
    offlineCache?: OfflineCache;
    /**
     * Van wie en welk bedrijf een GET-antwoord in de offline cache is. De scope
     * gaat bij de start van het verzoek in de sleutel, zodat een antwoord dat na
     * een wissel binnenkomt nooit onder de nieuwe omgeving terechtkomt. `null`:
     * niets uit de cache lezen en niets erin schrijven.
     */
    cacheScope?: () => string | null;
    /**
     * Een 401 op een verzoek dat een token droeg. `token` is het token dat werd
     * geweigerd - is dat inmiddels vervangen (wisselen, verlengen), dan zegt de
     * 401 niets over de huidige sessie.
     */
    onUnauthorized?: (error: ApiError, context: { token: string }) => void;
    timeoutMs?: number;
    retryAttempts?: number;
    retryBaseDelayMs?: number;
    retryMaxDelayMs?: number;
    sleep?: (ms: number) => Promise<void>;
}

const DEFAULTS = {
    timeoutMs: 15_000,
    retryAttempts: 3,
    retryBaseDelayMs: 500,
    retryMaxDelayMs: 8_000,
};

/**
 * Fetch-wrapper voor de Smartkeur API.
 *
 * - bearer-token en `X-Tenant` gaan automatisch mee,
 * - foutbodies worden naar `ApiError` vertaald (RFC 7807),
 * - 5xx-, netwerk- en timeoutfouten worden opnieuw geprobeerd met backoff,
 * - GET's worden gecachet en offline uit die cache geserveerd.
 *
 * Mutaties lopen *niet* rechtstreeks via deze client, maar via de outbox
 * (`shared/offline`). Alleen de sync-engine stuurt mutaties echt naar de API.
 */
export class HttpClient {
    constructor(private readonly options: HttpClientOptions) {}

    get<T>(path: string, options?: RequestOptions): Promise<T> {
        return this.request<T>('GET', path, options);
    }

    post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
        return this.request<T>('POST', path, { ...options, body });
    }

    put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
        return this.request<T>('PUT', path, { ...options, body });
    }

    patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
        return this.request<T>('PATCH', path, { ...options, body });
    }

    delete<T>(path: string, options?: RequestOptions): Promise<T> {
        return this.request<T>('DELETE', path, options);
    }

    async request<T>(method: HttpMethod, path: string, options: RequestOptions = {}): Promise<T> {
        const result = await this.requestWithMeta<T>(method, path, options);

        return result.data;
    }

    /** Zelfde als `request`, maar vertelt ook of het antwoord uit de cache kwam. */
    async requestWithMeta<T>(
        method: HttpMethod,
        path: string,
        options: RequestOptions = {},
    ): Promise<ApiResult<T>> {
        const url = this.buildUrl(path, options.query);
        const cacheKey = this.cacheKeyFor(method, url, options);
        const attempts =
            options.retryAttempts ?? this.options.retryAttempts ?? DEFAULTS.retryAttempts;

        if (!this.online()) {
            const cached = await this.readCache<T>(cacheKey);

            if (cached) {
                return cached;
            }

            throw ApiError.offline(path);
        }

        let lastError: ApiError | null = null;

        for (let attempt = 1; attempt <= Math.max(1, attempts); attempt += 1) {
            const token = this.tokenFor(options);

            try {
                const result = await this.send<T>(method, url, path, options, token);

                if (cacheKey) {
                    await this.options.offlineCache?.write(cacheKey, result.data);
                }

                return result;
            } catch (error) {
                if (!(error instanceof ApiError)) {
                    throw error;
                }

                lastError = error;

                if (error.kind === 'unauthorized' && token) {
                    this.options.onUnauthorized?.(error, { token });
                }

                const canRetry =
                    error.retryable &&
                    this.isRetryableRequest(method, options) &&
                    attempt < attempts;

                if (!canRetry) {
                    break;
                }

                await this.wait(error.retryAfterMs ?? this.backoff(attempt));
            }
        }

        const cached = await this.readCache<T>(cacheKey);

        if (cached && lastError && (lastError.kind === 'network' || lastError.kind === 'timeout')) {
            return cached;
        }

        throw lastError ?? ApiError.network(path);
    }

    private async send<T>(
        method: HttpMethod,
        url: string,
        path: string,
        options: RequestOptions,
        token: string | null,
    ): Promise<ApiResult<T>> {
        const fetchImpl = this.options.fetchImpl ?? globalThis.fetch;
        const timeoutMs = options.timeoutMs ?? this.options.timeoutMs ?? DEFAULTS.timeoutMs;
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);

        if (options.signal?.aborted) {
            controller.abort();
        } else if (options.signal) {
            options.signal.addEventListener('abort', () => controller.abort(), { once: true });
        }

        /*
         * De klok loopt tot en met het lezen van de body. Een verbinding die na de
         * kopregels stilvalt (een laptop die uit de slaap komt) hield het verzoek
         * anders voor altijd open, en daarmee de hele sync-ronde.
         */
        const failure = (error: unknown): unknown => {
            if (options.signal?.aborted) {
                return error;
            }

            return controller.signal.aborted
                ? ApiError.timeout(path, timeoutMs)
                : ApiError.network(path, error);
        };

        try {
            let response: Response;

            try {
                response = await fetchImpl(url, {
                    method,
                    headers: this.buildHeaders(method, options, token),
                    body: serializeBody(options.body),
                    signal: controller.signal,
                    credentials: 'omit',
                    mode: 'cors',
                });
            } catch (error) {
                throw failure(error);
            }

            if (!response.ok) {
                const body = await readBody(response);

                throw ApiError.fromProblem(
                    response.status,
                    toProblemDetails(response.status, body, path),
                    retryAfterMs(response),
                );
            }

            let data: unknown;

            try {
                data = await readSuccessBody(response, options.expect);
            } catch (error) {
                throw failure(error);
            }

            if (controller.signal.aborted) {
                throw failure(new DOMException('Het verzoek is afgebroken.', 'AbortError'));
            }

            return { data: data as T, status: response.status, fromCache: false };
        } finally {
            clearTimeout(timer);
        }
    }

    /** Het token dat met dit verzoek meegaat, of `null` voor een anoniem verzoek. */
    private tokenFor(options: RequestOptions): string | null {
        if (options.skipAuth) {
            return null;
        }

        return options.token ?? this.options.getToken?.() ?? null;
    }

    private buildHeaders(
        method: HttpMethod,
        options: RequestOptions,
        token: string | null,
    ): Headers {
        const headers = new Headers({ Accept: 'application/json' });

        if (options.body !== undefined && !(options.body instanceof FormData)) {
            headers.set('Content-Type', 'application/json');
        }

        if (!options.skipAuth) {
            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }

            const tenantId = options.token ? null : this.options.getTenantId?.();

            if (tenantId) {
                headers.set('X-Tenant', tenantId);
            }

            const deviceId = this.options.getDeviceId?.();

            if (deviceId) {
                headers.set('X-Device-Id', deviceId);
            }
        }

        if (options.idempotencyKey) {
            headers.set('Idempotency-Key', options.idempotencyKey);
        }

        for (const [name, value] of Object.entries(options.headers ?? {})) {
            headers.set(name, value);
        }

        if (method === 'GET') {
            headers.delete('Content-Type');
        }

        return headers;
    }

    private buildUrl(path: string, query?: Record<string, QueryValue>): string {
        const base = this.options.baseUrl.replace(/\/+$/, '');
        const suffix = path.startsWith('/') ? path : `/${path}`;
        const search = buildQueryString(query);

        return `${base}${suffix}${search}`;
    }

    private cacheKeyFor(method: HttpMethod, url: string, options: RequestOptions): string | null {
        if (options.cacheKey === false || method !== 'GET' || !this.options.offlineCache) {
            return null;
        }

        const key = options.cacheKey ?? url;

        if (!this.options.cacheScope) {
            return key;
        }

        const scope = this.options.cacheScope();

        return scope === null ? null : `${scope}|${key}`;
    }

    private async readCache<T>(key: string | null): Promise<ApiResult<T> | null> {
        if (!key || !this.options.offlineCache) {
            return null;
        }

        const cached = await this.options.offlineCache.read(key);

        return cached === undefined ? null : { data: cached as T, status: 200, fromCache: true };
    }

    /** POST mag alleen opnieuw als er een idempotency key is meegestuurd. */
    private isRetryableRequest(method: HttpMethod, options: RequestOptions): boolean {
        if (method === 'GET' || method === 'PUT' || method === 'DELETE') {
            return true;
        }

        return Boolean(options.idempotencyKey);
    }

    private backoff(attempt: number): number {
        const base = this.options.retryBaseDelayMs ?? DEFAULTS.retryBaseDelayMs;
        const max = this.options.retryMaxDelayMs ?? DEFAULTS.retryMaxDelayMs;

        return Math.min(max, base * 2 ** (attempt - 1));
    }

    private wait(ms: number): Promise<void> {
        return (this.options.sleep ?? defaultSleep)(ms);
    }

    private online(): boolean {
        return this.options.isOnline?.() ?? true;
    }
}

function defaultSleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export function buildQueryString(query?: Record<string, QueryValue>): string {
    if (!query) {
        return '';
    }

    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(query)) {
        if (value === undefined || value === null || value === '') {
            continue;
        }

        if (Array.isArray(value)) {
            value.forEach((entry) => params.append(`${key}[]`, String(entry)));
            continue;
        }

        params.set(key, String(value));
    }

    const search = params.toString();

    return search === '' ? '' : `?${search}`;
}

function serializeBody(body: unknown): BodyInit | undefined {
    if (body === undefined) {
        return undefined;
    }

    if (body instanceof FormData || body instanceof Blob || typeof body === 'string') {
        return body;
    }

    return JSON.stringify(body);
}

async function readBody(response: Response): Promise<unknown> {
    const text = await response.text().catch(() => '');

    if (text.trim() === '') {
        return null;
    }

    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

async function readSuccessBody(response: Response, expect: RequestOptions['expect']) {
    if (expect === 'none' || response.status === 204) {
        return null;
    }

    if (expect === 'blob') {
        return response.blob();
    }

    return readBody(response);
}

function retryAfterMs(response: Response): number | undefined {
    const header = response.headers.get('Retry-After');

    if (!header) {
        return undefined;
    }

    const seconds = Number(header);

    return Number.isFinite(seconds) ? seconds * 1_000 : undefined;
}
